import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendContactChangeOtp, verifyContactChangeOtp, formatPhoneNumber } from '../services/contactChangeService';
import './verification/verificationcss/Verification.css';
import AppHeader from '../components/AppHeader';
import Footer from '../components/AppFooter';

const ContactChangeOtpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpId, setOtpId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(60); // 1 minute in seconds
  const [isExpired, setIsExpired] = useState(false);
  const [attempts, setAttempts] = useState(3);
  const [devOtp, setDevOtp] = useState(null);
  const [verificationMethod, setVerificationMethod] = useState('sms');
  const [deliveryTarget, setDeliveryTarget] = useState('');

  // Get data from location state
  const { newPhoneNumber, currentPhoneNumber, onSuccess } = location.state || {};

  // Auto-focus first input when page loads
  useEffect(() => {
    if (otp[0] === '') {
      const firstInput = document.getElementById('otp-input-0');
      firstInput?.focus();
    }
  }, [otp]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Send OTP when page loads
  useEffect(() => {
    if (newPhoneNumber) {
      handleSendOtp();
    }
  }, [newPhoneNumber]);

  const handleSendOtp = async () => {
    setIsLoading(true);
    setError('');
    setDevOtp(null);

    try {
      const response = await sendContactChangeOtp(newPhoneNumber, currentPhoneNumber, verificationMethod);
      setOtpId(response.otpId);
      setDeliveryTarget(response.deliveryTarget || '');
      
      // Development mode - show OTP
      if (response.otp) {
        setDevOtp(response.otp);
        console.log('🔢 Development OTP:', response.otp);
      }
      
      setTimeLeft(60); // Reset timer
      setIsExpired(false);
      setAttempts(3);
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTryAnotherWay = async () => {
    if (verificationMethod === 'sms') {
      // User wants to try email instead - navigate to user-change-verification
      try {
        const pendingContactChange = JSON.parse(localStorage.getItem("pendingContactChange") || "{}");
        const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
        
        if (pendingContactChange && pendingContactChange.uid) {
          // Prepare data for UserChangeVerification (flow: profile-change)
          const pendingProfileChange = {
            uid: pendingContactChange.uid,
            currentEmail: loggedInUser.email,
            currentContact: pendingContactChange.currentContact,
            originalData: pendingContactChange.originalData,
            updatedData: pendingContactChange.tempData,
          };
          
          // Save to localStorage as expected by UserChangeVerification
          localStorage.setItem("pendingProfileChange", JSON.stringify(pendingProfileChange));
          
          // Navigate to user-change-verification
          navigate("/user-change-verification", {
            state: { 
              flow: "profile-change", 
              type: "profile-change",
              email: loggedInUser.email
            }
          });
          return;
        }
      } catch (err) {
        console.error("Failed to switch to email verification:", err);
      }
    }

    // Fallback for email to sms or if navigation fails
    const newMethod = verificationMethod === 'sms' ? 'email' : 'sms';
    setVerificationMethod(newMethod);
    setOtp(['', '', '', '', '', '']);
    setError('');
    setTimeLeft(60);
    setIsExpired(false);
    setAttempts(3);
    setDevOtp(null);
    
    // Auto-send OTP with new method
    await handleSendOtp();
  };

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      nextInput?.focus();
    }

    // Auto-verify when all digits are entered
    if (newOtp.every(digit => digit !== '')) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Check if it's a 6-digit number
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      
      // Focus last input
      const lastInput = document.getElementById('otp-input-5');
      lastInput?.focus();
      
      // Auto-verify
      handleVerifyOtp(pastedData);
    }
  };

  const handleVerifyOtp = async (otpCode) => {
    setIsVerifying(true);
    setError('');

    try {
      const response = await verifyContactChangeOtp(otpId, otpCode);
      
      if (response.success) {
        // Handle success callback using localStorage data
        const callbackData = localStorage.getItem("contactChangeCallback");
        if (callbackData) {
          const callback = JSON.parse(callbackData);
          
          // Update user data in localStorage
          const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
          
          // Update both root level and nested contactDetails if they exist
          const updatedUser = { 
            ...loggedInUser, 
            contact: response.newPhoneNumber 
          };
          
          if (updatedUser.contactDetails) {
            updatedUser.contactDetails = {
              ...updatedUser.contactDetails,
              contact: response.newPhoneNumber
            };
          }
          
          localStorage.setItem("user", JSON.stringify(updatedUser));
          
          // Clean up callback data
          localStorage.removeItem("contactChangeCallback");
          localStorage.removeItem("pendingContactChange");
          
          // Navigate back to user profile
          navigate('/user-home', { replace: true });
        } else {
          // Fallback: Navigate back to user profile
          navigate('/user-home', { replace: true });
        }
      }
    } catch (err) {
      const errorMessage = err.message || 'Invalid OTP';
      setError(errorMessage);
      
      // Clear OTP inputs
      setOtp(['', '', '', '', '', '']);
      
      // Focus first input
      setTimeout(() => {
        const firstInput = document.getElementById('otp-input-0');
        firstInput?.focus();
      }, 100);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!newPhoneNumber || !currentPhoneNumber) {
    return (
      <div className="otp-page">
        <AppHeader />
        <div className="otp-card">
          <h2>Missing Information</h2>
          <p style={{ fontSize: '14px', color: '#444', marginBottom: '38px' }}>
            Contact number information is missing. Please go back and try again.
          </p>
          <button onClick={handleBack} className="cancel-btn">Go Back</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <AppHeader />
      <div className="verification-page">
        <div className="otp-card">
          <div id="recaptcha-container" style={{ display: "none" }} />

          <h2>Verify New Phone Number</h2>
          
          <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '38px', textAlign: 'left' }}>
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#444' }}>
              Current: <span className="email">{currentPhoneNumber ? formatPhoneNumber(currentPhoneNumber) : 'Not set'}</span>
            </p>
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#444' }}>
              New: <span style={{ color: '#059669', fontWeight: '600' }}>{formatPhoneNumber(newPhoneNumber)}</span>
            </p>
          </div>

          {devOtp && (
            <div style={{ 
              backgroundColor: '#fef3c7', 
              border: '1px solid #f59e0b', 
              borderRadius: '8px', 
              padding: '12px', 
              marginBottom: '38px', 
              fontSize: '12px', 
              color: '#92400e' 
            }}>
              <strong>Development OTP:</strong> {devOtp}
            </div>
          )}

          <p style={{ fontSize: '14px', color: '#444', marginBottom: '38px' }}>
            Enter the 6-digit code sent via <strong className="email">{verificationMethod === 'sms' ? 'SMS' : 'Email'}</strong>
            {deliveryTarget && (
              <span> to {verificationMethod === 'sms' ? 
                `your current phone number ending in ${deliveryTarget.slice(-4)}` : 
                `your email ending in ${deliveryTarget.slice(-10).replace('@', '***@')}`
              }</span>
            )}
          </p>

          {/* OTP Input Fields */}
          <div className={`otp-inputs ${error ? "shake" : ""}`}>
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-input-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={error ? "error" : ""}
                disabled={isExpired || attempts === 0 || isVerifying}
                autoFocus={index === 0}
              />
            ))}
          </div>

          {/* Error Display */}
          {error && <div className="error-message">{error}</div>}

          {/* Timer Display */}
          <p className="resend-text timer-section">
            Code expires in{" "}
            <span className={`timer ${isExpired ? "expired" : ""}`}>
              {formatTime(timeLeft)}
            </span>
          </p>

          {/* Try Another Way */}
          <p className="resend-text resend-section">
            Prefer another method?{" "}
            <span 
              className="resend"
              onClick={handleTryAnotherWay}
              style={{ cursor: isLoading ? "default" : "pointer" }}
            >
              {isLoading ? 'Switching...' : `Try ${verificationMethod === 'sms' ? 'Email' : 'SMS'} instead`}
            </span>
          </p>

          {/* Resend OTP */}
          <p className="resend-text resend-section">
            Didn't receive a code?{" "}
            <span 
              className={`resend ${isExpired ? "enabled" : ""}`}
              onClick={handleSendOtp}
              style={{ cursor: isLoading ? "default" : "pointer" }}
            >
              {isLoading ? "Sending..." : (isExpired || attempts === 0 ? "Resend OTP" : "Resend Code")}
            </span>
          </p>

          {/* Verify Button */}
          <button
            onClick={() => handleVerifyOtp(otp.join(''))}
            disabled={!otp.every(digit => digit !== '') || isVerifying || isExpired || attempts === 0}
            className="verify-btn"
          >
            {isVerifying ? "Verifying..." : "Verify"}
          </button>

          {/* Cancel Option */}
          <button
            onClick={handleBack}
            className="cancel-btn"
          >
            Cancel
          </button>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ContactChangeOtpPage;
